import React from 'react';
import MainLayout from '../../layouts/MainLayout';
import Pagination from '../../components/Pagination';
import ExpedientListItem from './components/ExpedientListItem';
import ManagementPanel from './components/ManagementPanel/ManagementPanel';
import ExpedientDetailsPanel from './components/ExpedientDetailsPanel';
import AssignmentsSection from '../../components/AssignmentsSection/AssignmentsSection';
import EmptyState from './components/EmptyState';
import FilterButtons from './components/FilterButtons';
import LoadingSpinner from '../../components/LoadingSpinner';
import useManagementStore from '../../store/managementStore';
import { useAssignmentsFetch } from './hooks/useAssignmentsFetch';
import { useSocketManagement } from './hooks/useSocketManagement';
import { useExpedientSelection } from './hooks/useExpedientSelection';
import { getExpedientId } from './utils/assignmentUtils';

const ManagementList = () => {
  // Zustand store
  const { expedientsList, currentPage, totalPages, loading, setPagination } = useManagementStore();

  // Custom hooks (must be called before any conditional returns)
  // Initialize expedient selection first to get setSelectedExpedient
  const { selectedExpedient, setSelectedExpedient, handleExpedientSelect } = useExpedientSelection(
    null,
    expedientsList
  );
  // Initialize socket management with setSelectedExpedient
  const socket = useSocketManagement(currentPage, totalPages, setSelectedExpedient);
  // Re-initialize expedient selection with socket when available (hook uses ref internally)
  const { selectedExpedient: finalSelectedExpedient, handleExpedientSelect: finalHandleExpedientSelect } =
    useExpedientSelection(socket, expedientsList);

  useAssignmentsFetch(
    currentPage,
    finalSelectedExpedient || selectedExpedient,
    finalHandleExpedientSelect || handleExpedientSelect
  );

  // Use the final values for rendering
  const displaySelectedExpedient = finalSelectedExpedient || selectedExpedient;
  const displayHandleExpedientSelect = finalHandleExpedientSelect || handleExpedientSelect;

  // Check authentication
  const userToken = localStorage.getItem('token');
  if (userToken == null) {
    window.location = '/login';
    return null;
  }

  return (
    <MainLayout fullPage={true}>
      {/* Loading spinner overlay */}
      {loading && <LoadingSpinner message='Cargando expedientes...' fullScreen={true} />}

      {/* Dashboard */}
      <div className='h-full col-span-full xl:col-span-12 bg-white shadow-lg rounded-lg border border-slate-200'>
        <header className='px-5 py-4 border-b border-slate-100 flex justify-between items-center'>
          <div className='flex items-center gap-4'>
            <h1 className='text-2xl font-semibold text-slate-900'>Expedientes</h1>
            <FilterButtons />
          </div>
        </header>

        <div className='p-3'>
          {!Array.isArray(expedientsList) || expedientsList.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Ticketing System Layout - 3 Columns */}
              <div className='flex flex-col lg:flex-row gap-4 h-[calc(100vh-205px)]'>
                {/* Left Panel - Expedient List */}
                <div className='w-full lg:w-60 flex flex-col bg-white border border-slate-200 rounded-lg overflow-hidden shadow-lg'>
                  <div className='flex-1 overflow-y-auto'>
                    {expedientsList.map(expedient => (
                      <ExpedientListItem
                        key={expedient.expedient_id}
                        expedient={expedient}
                        isSelected={displaySelectedExpedient?.expedient_id === expedient.expedient_id}
                        onClick={() => displayHandleExpedientSelect(expedient)}
                      />
                    ))}
                  </div>
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className='border-t border-slate-200 p-2'>
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={page => setPagination(page, totalPages)}
                      />
                    </div>
                  )}
                </div>

                {/* Middle Panel - Management */}
                <div className='flex-1 min-w-0'>
                  <ManagementPanel expedient={displaySelectedExpedient} />
                </div>

                {/* Right Panel - Details & Actions */}
                <div className='w-full lg:w-[480px] flex-shrink-0 flex flex-col gap-4'>
                  <AssignmentsSection
                    expedientId={getExpedientId(displaySelectedExpedient) || displaySelectedExpedient?._id}
                    fullHeight={false}
                  />
                  <ExpedientDetailsPanel expedient={displaySelectedExpedient} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default ManagementList;
